import { useCallback, useEffect, useState } from "react";
import { X, User, Warehouse, MapPin, Package, Receipt, Boxes, AlertTriangle, Wrench } from "lucide-react";
import api, { inr, fmtDateTime, fmtErr } from "../lib/api";
import StatusBadge from "./StatusBadge";
import { toast } from "sonner";

function Row({ label, value, mono = false, testid }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex items-start justify-between gap-3 py-1" data-testid={testid}>
      <span className="shrink-0 text-xs text-slate-400">{label}</span>
      <span className={`text-right text-xs font-semibold text-shipany-navy ${mono ? "mono" : ""}`}>{value}</span>
    </div>
  );
}

function Section({ title, icon: Icon, children, testid, highlight = false }) {
  return (
    <div className={`rounded-xl border p-4 ${highlight ? "border-amber-200 bg-amber-50/40" : "border-slate-200 bg-slate-50/50"}`} data-testid={testid}>
      <h3 className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
        <Icon className="h-3.5 w-3.5 text-shipany-blue" /> {title}
      </h3>
      {children}
    </div>
  );
}

export default function AdminShipmentDetail({ shipmentId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [repairAwbs, setRepairAwbs] = useState("");
  const [repairing, setRepairing] = useState(false);

  const load = useCallback(() => {
    setData(null);
    setError("");
    api.get(`/admin/shipments/${shipmentId}/detail`)
      .then(({ data }) => {
        setData(data);
        const sh = data?.shipment || {};
        setRepairAwbs(((sh.provider_box_awbs || []).filter((a) => a !== sh.awb)).join(", "));
      })
      .catch((e) => { const m = fmtErr(e); setError(m); toast.error(m); });
  }, [shipmentId]);

  useEffect(() => { load(); }, [load]);

  const s = data?.shipment || {};
  const c = data?.customer || {};
  const pb = s.pricing_breakdown || {};
  const cost = pb.provider_cost || 0;
  const margin = s.status !== "CANCELLED" ? (s.price || 0) - cost : 0;
  const events = [...(s.tracking_events || [])].sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""));
  const expectedBoxes = Math.max(Number(s.boxes || 1), (s.box_details || []).length, 1);
  const needsMpsRepair = s.provider_code === "shipmozo" && expectedBoxes > 1 && Number(s.label_count || 0) < expectedBoxes;

  const saveMpsAwbs = async () => {
    setRepairing(true);
    try {
      const childAwbs = repairAwbs.split(/[,\s]+/).map((a) => a.trim()).filter(Boolean);
      const { data: result } = await api.post(`/admin/shipments/${shipmentId}/mps-box-awbs`, { box_awbs: [s.awb, ...childAwbs] });
      if (result.labels_fetched) toast.success(result.message);
      else toast.warning(result.message, { duration: 7000 });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setRepairing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-shipany-navy/40 p-4" onClick={onClose} data-testid="shipment-detail-overlay">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="shipment-detail-modal">
        {!data && !error && <p className="py-16 text-center text-sm text-slate-400">Loading shipment…</p>}
        {error && <p className="py-16 text-center text-sm text-red-500" data-testid="shipment-detail-error">{error}</p>}

        {data && (
          <>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="mono text-[10px] text-slate-400">{s.order_number || s.order_id}{s.customer_reference ? ` · ref ${s.customer_reference}` : ""}</p>
                <h2 className="mono text-xl font-bold text-shipany-navy" data-testid="sd-awb">{s.awb || "AWB pending"}</h2>
                <p className="text-xs text-slate-500">
                  {s.courier_name} · {s.origin_city} → {s.destination_city} · {s.weight}kg
                  {s.source === "api" && <span className="ml-1.5 rounded bg-cyan-100 px-1.5 py-0.5 text-[9px] font-bold text-cyan-700">API</span>}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={s.status} />
                <button onClick={onClose} data-testid="shipment-detail-close" className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"><X className="h-4 w-4" /></button>
              </div>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              <Section title="Customer" icon={User} testid="sd-customer">
                <Row label="Name" value={c.name || s.customer_name} testid="sd-customer-name" />
                <Row label="Email" value={c.email || s.customer_email} />
                <Row label="Phone" value={c.phone} mono />
                <Row label="Company" value={c.company} />
                <Row label="KYC" value={c.kyc_status} />
              </Section>

              <Section title="Sender (Pickup)" icon={Warehouse} testid="sd-sender">
                <Row label="Warehouse" value={s.warehouse_id} mono />
                <Row label="Address" value={s.pickup_address} testid="sd-pickup-address" />
              </Section>

              <Section title="Receiver (Delivery) — verify customer entry" icon={MapPin} testid="sd-receiver" highlight>
                <Row label="Name" value={s.delivery_name} testid="sd-delivery-name" />
                <Row label="Phone" value={s.delivery_phone} mono testid="sd-delivery-phone" />
                <Row label="Alt phone" value={s.delivery_alt_phone} mono />
                <Row label="Email" value={s.delivery_email} />
                <Row label="Address" value={s.delivery_address} testid="sd-delivery-address" />
                <Row label="City / State" value={[s.delivery_city, s.delivery_state].filter(Boolean).join(", ")} />
                <Row label="Pincode" value={s.delivery_pincode} mono testid="sd-delivery-pincode" />
                <Row label="GSTIN" value={s.receiver_gstin} mono />
              </Section>

              <Section title="Package" icon={Package} testid="sd-package">
                <Row label="Type" value={s.package_type} />
                <Row label="Weight" value={`${s.weight} kg`} mono />
                {s.length ? <Row label="Dims (cm)" value={`${s.length} × ${s.breadth} × ${s.height}`} mono /> : null}
                <Row label="Boxes" value={s.boxes} mono />
                <Row label="Declared value" value={s.declared_value != null ? inr(s.declared_value) : ""} mono />
                <Row label="Payment" value={`${s.payment_mode}${s.payment_mode === "COD" ? ` · collect ${inr(s.cod_amount || 0)}` : ""}`} testid="sd-payment" />
                {(s.box_details || []).map((b, i) => (
                  <Row key={i} label={`Box ${i + 1}`} value={`${b.weight}kg · ${b.length}×${b.breadth}×${b.height}cm`} mono testid={`sd-box-${i}`} />
                ))}
              </Section>

              <Section title={`Items (${(s.items || []).length})`} icon={Boxes} testid="sd-items">
                {(s.items || []).length === 0 && <p className="text-xs text-slate-400">No item details recorded.</p>}
                {(s.items || []).map((it, i) => (
                  <div key={i} className="flex items-center justify-between py-1 text-xs" data-testid={`sd-item-${i}`}>
                    <span className="font-semibold text-shipany-navy">{it.name}{it.sku ? <span className="mono text-[10px] text-slate-400"> · {it.sku}</span> : ""}</span>
                    <span className="mono text-slate-600">×{it.qty} · {inr(it.price || 0)}</span>
                  </div>
                ))}
              </Section>

              <Section title="Pricing (internal)" icon={Receipt} testid="sd-pricing">
                <Row label="Customer charged" value={inr(s.price || 0)} testid="sd-price" />
                <Row label="Courier base" value={pb.base_courier_price != null ? inr(pb.base_courier_price) : ""} mono />
                <Row label="Markup" value={pb.markup_pct != null ? `${pb.markup_pct}% = ${inr(pb.markup_pct_amt || 0)} + ${inr(pb.fixed_markup || 0)} fixed` : ""} mono />
                {pb.min_margin ? <Row label="Min margin adj." value={inr(pb.min_margin)} mono /> : null}
                {pb.gst ? <Row label="GST" value={inr(pb.gst)} mono /> : null}
                <Row label="Provider cost" value={inr(cost)} mono testid="sd-cost" />
                <Row label="Margin" value={inr(margin)} mono testid="sd-margin" />
                {data.invoice && <Row label="Invoice" value={`${data.invoice.invoice_number || ""} · ${inr(data.invoice.total || 0)} · ${data.invoice.status || ""}`} testid="sd-invoice" />}
              </Section>

              <Section title="Operations" icon={AlertTriangle} testid="sd-ops">
                <Row label="Shipment ID" value={s.shipment_id} mono />
                <Row label="Provider" value={s.provider_code} mono />
                <Row label="Provider refs" value={[s.provider_shipment_id, s.provider_order_id].filter(Boolean).join(" / ")} mono />
                <Row label="Box AWBs" value={(s.provider_box_awbs || []).join(", ")} mono testid="sd-box-awbs" />
                <Row label="Routing" value={s.routing_summary ? `${s.routing_summary.hops ?? ""} hop(s)${s.routing_summary.failover ? " · failover" : ""}` : ""} />
                <Row label="RTO risk" value={s.rto_risk ? `${s.rto_risk.level} (${Math.round((s.rto_risk.probability || 0) * 100)}%)` : ""} />
                <Row label="Created" value={fmtDateTime(s.created_at)} />
                <Row label="ETA" value={s.eta_date} />
                {s.cancelled_at && <Row label="Cancelled" value={fmtDateTime(s.cancelled_at)} />}
              </Section>

              {needsMpsRepair && (
                <Section title="MPS label repair" icon={Wrench} testid="sd-mps-repair" highlight>
                  <p className="mb-2 text-xs text-amber-700">Only {s.label_count || 0} of {expectedBoxes} box labels are available. Paste the child AWB shown in the Shipmozo panel, then save to fetch all labels.</p>
                  <input value={repairAwbs} onChange={(e) => setRepairAwbs(e.target.value)} data-testid="mps-child-awbs-input"
                    placeholder="Child AWB(s), comma separated" className="mono w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-xs focus:border-shipany-blue focus:outline-none" />
                  <button onClick={saveMpsAwbs} disabled={repairing} data-testid="repair-mps-labels-btn"
                    className="mt-2 w-full rounded-xl bg-shipany-blue px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-[#0648B3] disabled:opacity-50">
                    {repairing ? "Saving & fetching…" : "Save child AWB & fetch labels"}
                  </button>
                </Section>
              )}

              <Section title="Tracking Timeline" icon={MapPin} testid="sd-tracking">
                {events.length === 0 && <p className="text-xs text-slate-400">No courier scans yet — live tracking syncs every 15 minutes.</p>}
                <ol className="relative max-h-56 space-y-3 overflow-y-auto border-l-2 border-slate-200 pl-4">
                  {events.map((e, i) => (
                    <li key={i} className="relative" data-testid={`sd-event-${i}`}>
                      <span className={`absolute -left-[22px] top-1 h-2.5 w-2.5 rounded-full border-2 border-white ${i === 0 ? "bg-shipany-blue" : "bg-slate-300"}`} />
                      <div className="flex items-center gap-2">
                        <StatusBadge status={e.status} />
                        <span className="text-[10px] text-slate-400">{fmtDateTime(e.timestamp)}</span>
                      </div>
                      {e.note && <p className="mt-0.5 text-xs text-slate-600">{e.note}</p>}
                      {e.location && <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{e.location}</p>}
                    </li>
                  ))}
                </ol>
              </Section>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
